import { TABLE_RELATIONSHIPS, TABLE_DEFINITIONS } from './schemaDefinitions';

/**
 * Builds the FROM clause with necessary INNER JOINs based on selected tables
 * Uses the first selected table as the base table
 *
 * @param {Array<string>} requiredTables - Array of table names needed (e.g., ['tblPrt', 'tblEpd'])
 * @returns {string} FROM clause with INNER JOINs (e.g., "tblPrt\nINNER JOIN tblEpd ON...")
 */
const buildFromClause = (requiredTables) => {
  // If no tables or only one table, no joins needed
  if (!requiredTables || requiredTables.length === 0) {
    return '';
  }
  if (requiredTables.length === 1) {
    return requiredTables[0];
  }

  // Use first table as the base
  const baseTable = requiredTables[0];
  const tablesToJoin = requiredTables.slice(1);

  // Track which tables have been joined
  const joinedTables = new Set([baseTable]);
  const joinStatements = [];

  // Keep trying to join tables until all are connected
  let progress = true;
  while (tablesToJoin.length > 0 && progress) {
    progress = false;

    for (let i = tablesToJoin.length - 1; i >= 0; i--) {
      const targetTable = tablesToJoin[i];

      // Try to find a relationship between any joined table and this target table
      const relationship = TABLE_RELATIONSHIPS.find(rel => {
        // Check if relationship connects a joined table to the target
        return (
          (joinedTables.has(rel.table1) && rel.table2 === targetTable) ||
          (joinedTables.has(rel.table2) && rel.table1 === targetTable)
        );
      });

      if (relationship) {
        // Found a connection - add the JOIN
        joinStatements.push(`INNER JOIN ${targetTable} ON ${relationship.joinCondition}`);
        joinedTables.add(targetTable);
        tablesToJoin.splice(i, 1);
        progress = true;
      }
    }
  }

  // Error handling: if tables remain unjoined, throw error
  if (tablesToJoin.length > 0) {
    throw new Error(`Cannot join tables: ${tablesToJoin.join(', ')} - no relationship path found from base table ${baseTable}. Check TABLE_RELATIONSHIPS in schemaDefinitions.js.`);
  }

  // Build the final FROM clause
  let fromClause = baseTable;
  if (joinStatements.length > 0) {
    fromClause += '\n' + joinStatements.join('\n');
  }

  return fromClause;
};

// Generate SQL query from selected fields
export const generateSql = (selectedFields) => {
  if (!selectedFields || selectedFields.length === 0) {
    return '';
  }

  // Build SELECT clause
  const selectFields = selectedFields
    .map(field => {
      const fullFieldName = `${field.tableName}.${field.fieldName}`;
      return `  ${fullFieldName} AS '${field.fieldFriendlyName}'`;
    })
    .join(',\n');

  // Build WHERE clause
  const whereClauses = selectedFields
    .filter(field => field.criteria || ['isNull', 'isNotNull'].includes(field.operator))
    .map(field => generateWhereClause(field));

  const validWhereClauses = whereClauses.filter(clause => clause !== null);

  const whereSection = validWhereClauses.length > 0
    ? `WHERE\n  ${validWhereClauses.join('\n  AND ')}`
    : '';

  // Extract unique table names from selected fields
  const requiredTables = [...new Set(selectedFields.map(field => field.tableName).filter(Boolean))];

  // Build dynamic FROM clause
  const fromClause = buildFromClause(requiredTables);

  // Build final SQL
  const sql = `SELECT\n${selectFields}\nFROM ${fromClause}${whereSection ? `\n${whereSection}` : ''}`;

  return sql;
};

// Generate WHERE clause for a single field
const generateWhereClause = (field) => {
  if (!field.operator) {
    return null;
  }

  const fullFieldName = `${field.tableName}.${field.fieldName}`;
  const { dataType, operator, criteria } = field;

  // Operators that don't require criteria
  if (operator === 'isNull') {
    return `${fullFieldName} IS NULL`;
  }

  if (operator === 'isNotNull') {
    return `${fullFieldName} IS NOT NULL`;
  }

  // All other operators require criteria
  if (!criteria) {
    return null;
  }

  const isStringType = dataType === 'string';
  const isDateType = dataType === 'datetime';

  switch (operator) {
    case 'equals':
      return isStringType
        ? `${fullFieldName} = '${escapeSql(criteria)}'`
        : isDateType
        ? `${fullFieldName} = '${criteria}'`
        : `${fullFieldName} = ${criteria}`;

    case 'notEquals':
      return isStringType
        ? `${fullFieldName} != '${escapeSql(criteria)}'`
        : isDateType
        ? `${fullFieldName} != '${criteria}'`
        : `${fullFieldName} != ${criteria}`;

    case 'greaterThan':
      return isDateType
        ? `${fullFieldName} > '${criteria}'`
        : `${fullFieldName} > ${criteria}`;

    case 'lessThan':
      return isDateType
        ? `${fullFieldName} < '${criteria}'`
        : `${fullFieldName} < ${criteria}`;

    case 'greaterThanOrEqual':
      return isDateType
        ? `${fullFieldName} >= '${criteria}'`
        : `${fullFieldName} >= ${criteria}`;

    case 'lessThanOrEqual':
      return isDateType
        ? `${fullFieldName} <= '${criteria}'`
        : `${fullFieldName} <= ${criteria}`;

    case 'contains':
      return `${fullFieldName} LIKE '%${escapeSql(criteria)}%'`;

    case 'startsWith':
      return `${fullFieldName} LIKE '${escapeSql(criteria)}%'`;

    case 'endsWith':
      return `${fullFieldName} LIKE '%${escapeSql(criteria)}'`;

    case 'between': {
      // Expect criteria format: "value1,value2" or "value1, value2"
      const parts = criteria.split(',').map(p => p.trim());
      if (parts.length !== 2) {
        return null;
      }
      const [start, end] = parts;
      return isDateType
        ? `${fullFieldName} BETWEEN '${start}' AND '${end}'`
        : isStringType
        ? `${fullFieldName} BETWEEN '${escapeSql(start)}' AND '${escapeSql(end)}'`
        : `${fullFieldName} BETWEEN ${start} AND ${end}`;
    }

    case 'in': {
      // Expect criteria format: "value1,value2,value3" or "value1, value2, value3"
      const values = criteria.split(',').map(v => v.trim()).filter(v => v);
      if (values.length === 0) {
        return null;
      }
      const formattedValues = isStringType
        ? values.map(v => `'${escapeSql(v)}'`).join(', ')
        : values.join(', ');
      return `${fullFieldName} IN (${formattedValues})`;
    }

    default:
      return null;
  }
};

// Escape SQL special characters to prevent injection
const escapeSql = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  // Replace single quotes with two single quotes (SQL escape method)
  return value.replace(/'/g, "''");
};

// Validate SQL query (basic validation)
export const validateSql = (sql) => {
  const errors = [];

  if (!sql || sql.trim().length === 0) {
    errors.push('Query is empty');
    return { isValid: false, errors };
  }

  // Check for basic SQL structure
  if (!sql.toUpperCase().includes('SELECT')) {
    errors.push('Query must contain SELECT clause');
  }

  if (!sql.toUpperCase().includes('FROM')) {
    errors.push('Query must contain FROM clause');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Format SQL for display (add line breaks and indentation)
export const formatSql = (sql) => {
  if (!sql) return '';

  // Already formatted in generateSql, but can add additional formatting here
  return sql;
};

// Parse criteria based on operator requirements
export const parseCriteria = (criteria, operator, dataType) => {
  if (!criteria) return null;

  switch (operator) {
    case 'between':
      // Should be "value1,value2"
      const parts = criteria.split(',').map(p => p.trim());
      return parts.length === 2 ? criteria : null;

    case 'in':
      // Should be "value1,value2,value3"
      const values = criteria.split(',').map(v => v.trim()).filter(v => v);
      return values.length > 0 ? criteria : null;

    default:
      return criteria;
  }
};
